import { useEffect, useState } from "react";
import axios from "axios";
import CITIES from "@/data/loiret-cities";
import PARIS_CITIES from "@/data/paris-cities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ab_admin_token";

// Liste centralisée des pages SEO villes Loiret (Orléans + villes du Loiret)
const SEO_CITY_PAGES = [
    { name: "Orléans", postal: "45000", path: "/serrurier-orleans-45" },
    { name: "Charenton-le-Pont", postal: "94220", path: "/serrurier-charenton-le-pont-94" },
    ...CITIES.map((c) => ({ name: c.name, postal: c.postal, path: `/serrurier-${c.slug}-45` })),
];

// Liste des 20 arrondissements Paris + 3 pages spéciales
const SEO_PARIS_PAGES = PARIS_CITIES.map((c) => ({ name: c.name, postal: c.postal, path: `/${c.slug}` }));

const fmt = (d) => new Date(d).toLocaleString("fr-FR");

const Stat = ({ label, value, onClick, active, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`text-left bg-white border rounded p-3 transition-all ${
            onClick ? "cursor-pointer hover:border-[#E63946] hover:shadow-sm" : "cursor-default"
        } ${active ? "border-[#E63946] ring-2 ring-[#E63946]/20 bg-[#FFF5F6]" : "border-zinc-200"}`}
    >
        <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">{label}</div>
        <div className="font-display font-bold text-lg mt-1">{value}</div>
    </button>
);

// --- Helpers de rendu pour le tableau Visites (évite les ternaires imbriqués) ---
const renderPositionCell = (v) => {
    if (v.gps_lat != null && v.gps_lon != null) {
        return (
            <a
                href={`https://www.google.com/maps?q=${v.gps_lat},${v.gps_lon}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-100 text-green-900 hover:bg-green-200 px-2 py-0.5 rounded text-xs font-semibold transition-colors"
                title={`GPS précis · Lat ${v.gps_lat.toFixed(5)}, Lon ${v.gps_lon.toFixed(5)} — cliquer pour voir sur Maps`}
            >
                📍 {v.gps_city || `${v.gps_lat.toFixed(3)}, ${v.gps_lon.toFixed(3)}`}
                {v.gps_postal ? ` ${v.gps_postal}` : ""}
            </a>
        );
    }
    if (v.ip_lat != null && v.ip_lon != null) {
        return (
            <a
                href={`https://www.google.com/maps?q=${v.ip_lat},${v.ip_lon}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-100 text-blue-900 hover:bg-blue-200 px-2 py-0.5 rounded text-xs font-semibold transition-colors"
                title={`Position via IP · Lat ${v.ip_lat.toFixed(3)}, Lon ${v.ip_lon.toFixed(3)}`}
            >
                🌐 {v.ip_city || `${v.ip_lat.toFixed(2)}, ${v.ip_lon.toFixed(2)}`}
                {v.ip_postal ? ` ${v.ip_postal}` : ""}
            </a>
        );
    }
    return <span className="text-zinc-400 text-[10px]">—</span>;
};

const renderSourceCell = (v, adsFlag) => {
    if (adsFlag) {
        return <span className="inline-block bg-[#E63946] text-white px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold">Google Ads</span>;
    }
    if (v.utm_source) {
        return <span className="text-zinc-700">{v.utm_source}</span>;
    }
    if (v.referer) {
        return <span className="text-zinc-500" title={v.referer}>{new URL(v.referer).hostname}</span>;
    }
    return <span className="text-zinc-400">direct</span>;
};

const renderKeywordCell = (v, adsFlag) => {
    if (v.search_keyword) {
        return <span className="inline-block bg-yellow-100 text-yellow-900 px-2 py-0.5 rounded text-xs font-semibold">{v.search_keyword}</span>;
    }
    let placeholder = "—";
    if (adsFlag) placeholder = "À paramétrer dans Google Ads";
    else if (v.referer && v.referer.includes("google.")) placeholder = "Google ne le transmet pas";
    return <span className="text-zinc-400 text-[10px]">{placeholder}</span>;
};

// Détecte si le FAI est un opérateur mobile français (ville géolocalisée souvent imprécise)
const MOBILE_ISP_PATTERNS = [
    /orange\s+s\.?a\.?\b/i,      // Orange S.A. / Orange SA (mobile)
    /\bfree\s*mobile\b/i,         // Free Mobile SAS
    /\bbouygues\b/i,              // Bouygues Telecom (mobile)
    /\bsfr\s+mobile\b/i,          // SFR Mobile
    /\bnrj\s*mobile\b/i,
    /\blyca\s*mobile\b/i,
    /\blebara\b/i,
    /\bsosh\b/i,
    /\bred\s+by\s+sfr\b/i,
    /\bmobile\b/i,                // tout ISP contenant "mobile" en filet de sécurité
];
const isMobileIsp = (isp) => {
    if (!isp) return false;
    return MOBILE_ISP_PATTERNS.some((re) => re.test(isp));
};

const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
    const [draft, setDraft] = useState("");
    const [tab, setTab] = useState("contacts");
    const [contacts, setContacts] = useState([]);
    const [visits, setVisits] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD or "" (all)
    const [quickView, setQuickView] = useState(null); // null | "uniqueIps" | "ads"

    const fetchAll = async (t) => {
        setLoading(true);
        setError("");
        try {
            const headers = { Authorization: `Bearer ${t}` };
            const [c, v, k] = await Promise.all([
                axios.get(`${API}/contact`, { headers }),
                axios.get(`${API}/visits`, { headers }),
                axios.get(`${API}/keyword-stats`, { headers }),
            ]);
            setContacts(c.data || []);
            setVisits(v.data || []);
            setKeywords(k.data || []);
            localStorage.setItem(STORAGE_KEY, t);
        } catch {
            setError("Token invalide ou serveur indisponible.");
            setContacts([]); setVisits([]); setKeywords([]);
            localStorage.removeItem(STORAGE_KEY);
            setToken("");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (token) fetchAll(token); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

    const logout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setToken(""); setContacts([]); setVisits([]); setKeywords([]);
    };

    const items = tab === "contacts" ? contacts : tab === "visits" ? visits : keywords;
    const sameDay = (iso, ymd) => {
        if (!ymd) return true;
        const d = new Date(iso);
        const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return local === ymd;
    };
    // Counts per IP across all visits (respecting date filter only, not quickView/filter)
    const ipCounts = visits.reduce((acc, v) => {
        if (!sameDay(v.created_at, dateFilter)) return acc;
        if (v.ip) acc[v.ip] = (acc[v.ip] || 0) + 1;
        return acc;
    }, {});
    // Counts per IP for ads clicks only (used in "Clics pubs" view)
    const ipAdsCounts = visits.reduce((acc, v) => {
        if (!sameDay(v.created_at, dateFilter)) return acc;
        const isAds = !!(v.gclid || v.utm_source === "google" || v.utm_medium === "cpc");
        if (isAds && v.ip) acc[v.ip] = (acc[v.ip] || 0) + 1;
        return acc;
    }, {});
    const filtered = items.filter((it) => {
        if (tab !== "keywords" && !sameDay(it.created_at, dateFilter)) return false;
        if (tab === "visits" && quickView === "ads") {
            const isAds = !!(it.gclid || it.utm_source === "google" || it.utm_medium === "cpc");
            if (!isAds) return false;
        }
        if (!filter.trim()) return true;
        const f = filter.toLowerCase();
        let fields;
        if (tab === "contacts") fields = [it.name, it.phone, it.email, it.city, it.ip_city, it.ip, it.service];
        else if (tab === "visits") fields = [it.ip, it.ip_city, it.ip_country, it.ip_isp, it.path, it.referer, it.utm_source, it.utm_campaign, it.search_keyword];
        else fields = [it.keyword, it.city, ...(it.paths || [])];
        return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(f));
    });
    // Post-filter: dedupe by IP when quickView === "uniqueIps"
    const finalRows = (tab === "visits" && quickView === "uniqueIps")
        ? Object.values(filtered.reduce((acc, v) => {
            if (v.ip && !acc[v.ip]) acc[v.ip] = v;
            return acc;
        }, {}))
        : filtered;

    // Quick date shortcuts (local timezone)
    const todayYMD = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
    const yesterdayYMD = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();

    // Stats — restricted to dateFilter (current selected day or all)
    const dayContacts = contacts.filter((c) => sameDay(c.created_at, dateFilter));
    const dayVisits = visits.filter((v) => sameDay(v.created_at, dateFilter));
    const dayUniqueIPs = new Set(dayVisits.map((v) => v.ip).filter(Boolean)).size;
    const dayAdsClicks = dayVisits.filter((v) => v.gclid || v.utm_source === "google" || v.utm_medium === "cpc").length;
    const statsLabelSuffix = dateFilter
        ? ` · ${new Date(dateFilter).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`
        : "";

    const deleteItem = async (item) => {
        const kind = tab === "contacts" ? "contact" : "visite";
        if (!window.confirm(`Supprimer définitivement cette ${kind} ?`)) return;
        try {
            const endpoint = tab === "contacts" ? `${API}/contact/${item.id}` : `${API}/visits/${item.id}`;
            await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
            if (tab === "contacts") setContacts((arr) => arr.filter((x) => x.id !== item.id));
            else setVisits((arr) => arr.filter((x) => x.id !== item.id));
        } catch {
            alert("Erreur lors de la suppression.");
        }
    };

    const exportCsv = () => {
        let rows;
        if (tab === "contacts") {
            rows = [
                ["Date", "Nom", "Téléphone", "Email", "Ville client", "Service", "Message", "IP", "Ville IP", "Région IP", "Pays IP", "FAI"],
                ...finalRows.map((it) => [
                    fmt(it.created_at), it.name, it.phone, it.email, it.city, it.service,
                    (it.message || "").replace(/\n/g, " "), it.ip, it.ip_city, it.ip_region, it.ip_country, it.ip_isp,
                ]),
            ];
        } else if (tab === "keywords") {
            rows = [
                ["Mot-clé", "Ville visiteur", "Visites", "Dont Google Ads", "Pages visitées", "Première visite", "Dernière visite"],
                ...finalRows.map((it) => [
                    it.keyword, it.city, it.count, it.ads_count, (it.paths || []).join(" | "),
                    fmt(it.first_seen), fmt(it.last_seen),
                ]),
            ];
        } else {
            rows = [
                ["Date", "IP", "Ville IP", "Région", "Pays", "FAI", "Position GPS (lat,lon)", "Ville GPS", "CP GPS", "Page visitée", "Referer", "Source UTM", "Campagne", "GCLID", "Mot-clé", "User-Agent"],
                ...finalRows.map((it) => [
                    fmt(it.created_at), it.ip, it.ip_city, it.ip_region, it.ip_country, it.ip_isp,
                    (it.gps_lat != null && it.gps_lon != null) ? `${it.gps_lat},${it.gps_lon}` : "",
                    it.gps_city || "", it.gps_postal || "",
                    it.path, it.referer, it.utm_source, it.utm_campaign, it.gclid, it.search_keyword || "", it.user_agent,
                ]),
            ];
        }
        const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // (Stats are computed per-day above as dayContacts/dayVisits/dayUniqueIPs/dayAdsClicks)

    if (!token) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
                <form onSubmit={(e) => { e.preventDefault(); setToken(draft.trim()); }}
                    className="bg-white border border-zinc-200 rounded-lg p-6 w-full max-w-sm" data-testid="admin-login">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E63946] mb-2">◉ Espace privé</div>
                    <h1 className="font-display font-bold uppercase text-xl tracking-tight mb-5">Admin Atelier Beauchamp</h1>
                    <label className="text-xs uppercase tracking-wide font-semibold text-zinc-600">Token d'accès</label>
                    <input type="password" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                        placeholder="Saisir le token admin"
                        className="mt-2 w-full border border-zinc-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-black"
                        data-testid="admin-token-input" />
                    {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
                    <button type="submit"
                        className="mt-4 w-full bg-[#0A0A0A] hover:bg-[#E63946] text-white py-3 rounded text-sm font-semibold uppercase tracking-wide transition-colors"
                        data-testid="admin-login-btn">Se connecter</button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
            <header className="bg-[#0A0A0A] text-white px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E63946]">◉ Admin Atelier Beauchamp</div>
                    <h1 className="font-display font-bold uppercase text-lg sm:text-xl tracking-tight">Tableau de bord</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => fetchAll(token)} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-2 rounded uppercase tracking-wide font-semibold">↻ Actualiser</button>
                    <button onClick={exportCsv} className="text-xs bg-white text-black hover:bg-zinc-200 px-3 py-2 rounded uppercase tracking-wide font-semibold">⬇ Export CSV</button>
                    <button onClick={logout} className="text-xs bg-[#E63946] hover:bg-[#D62828] px-3 py-2 rounded uppercase tracking-wide font-semibold">Déconnexion</button>
                </div>
            </header>

            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    <Stat
                        label={`Contacts${statsLabelSuffix}`}
                        value={dayContacts.length}
                        testid="stat-contacts"
                        active={tab === "contacts"}
                        onClick={() => { setTab("contacts"); setQuickView(null); }}
                    />
                    <Stat
                        label={`Visites${statsLabelSuffix}`}
                        value={dayVisits.length}
                        testid="stat-visits"
                        active={tab === "visits" && quickView === null}
                        onClick={() => { setTab("visits"); setQuickView(null); }}
                    />
                    <Stat
                        label={`IP uniques${statsLabelSuffix}`}
                        value={dayUniqueIPs}
                        testid="stat-unique-ips"
                        active={tab === "visits" && quickView === "uniqueIps"}
                        onClick={() => { setTab("visits"); setQuickView("uniqueIps"); }}
                    />
                    <Stat
                        label={`Clics pubs${statsLabelSuffix}`}
                        value={dayAdsClicks}
                        testid="stat-ads-clicks"
                        active={tab === "visits" && quickView === "ads"}
                        onClick={() => { setTab("visits"); setQuickView("ads"); }}
                    />
                </div>

                {/* Villes Loiret — Liens directs vers les 11 pages SEO */}
                <div className="bg-white border border-zinc-200 rounded p-4 mb-5" data-testid="cities-loiret-card">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">◉ Pages SEO</div>
                            <h2 className="font-display font-bold uppercase text-base tracking-tight">Villes Loiret</h2>
                        </div>
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">{SEO_CITY_PAGES.length} pages</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {SEO_CITY_PAGES.map((c) => (
                            <a
                                key={c.path}
                                href={c.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center justify-between gap-2 bg-zinc-50 hover:bg-[#E63946] hover:text-white border border-zinc-200 hover:border-[#E63946] rounded px-3 py-2.5 transition-colors"
                                data-testid={`city-link-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-display font-bold text-xs truncate">Serrurier {c.name}</div>
                                    <div className="text-[9px] uppercase tracking-wide text-zinc-500 group-hover:text-white/80 font-semibold">{c.postal}</div>
                                </div>
                                <span className="text-zinc-400 group-hover:text-white text-xs flex-shrink-0">↗</span>
                            </a>
                        ))}
                    </div>
                </div>

                {/* Arrondissements Paris — 20 pages SEO */}
                <div className="bg-white border border-zinc-200 rounded p-4 mb-5" data-testid="cities-paris-card">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">◉ Pages SEO</div>
                            <h2 className="font-display font-bold uppercase text-base tracking-tight">Arrondissements Paris</h2>
                        </div>
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">{SEO_PARIS_PAGES.length} pages</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                        {SEO_PARIS_PAGES.map((c) => (
                            <a
                                key={c.path}
                                href={c.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center justify-between gap-2 bg-zinc-50 hover:bg-[#0055A4] hover:text-white border border-zinc-200 hover:border-[#0055A4] rounded px-3 py-2.5 transition-colors"
                                data-testid={`paris-link-${c.postal}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-display font-bold text-xs truncate">{c.name}</div>
                                    <div className="text-[9px] uppercase tracking-wide text-zinc-500 group-hover:text-white/80 font-semibold">{c.postal}</div>
                                </div>
                                <span className="text-zinc-400 group-hover:text-white text-xs flex-shrink-0">↗</span>
                            </a>
                        ))}
                    </div>
                </div>

                {quickView && (
                    <div className="mb-4 flex items-center gap-2 bg-[#FFF5F6] border border-[#E63946]/30 rounded p-3 text-xs">
                        <span className="font-semibold text-[#E63946] uppercase tracking-wide">
                            {quickView === "uniqueIps" ? "✓ Vue : 1 ligne par IP unique" : "✓ Vue : Clics depuis Google Ads uniquement"}
                        </span>
                        <button
                            onClick={() => setQuickView(null)}
                            className="ml-auto bg-white border border-zinc-300 hover:border-black px-3 py-1 rounded uppercase tracking-wide font-semibold transition-colors"
                        >
                            ✕ Retirer ce filtre
                        </button>
                    </div>
                )}

                <div className="flex gap-2 mb-4 border-b border-zinc-200">
                    {[
                        { k: "contacts", l: `Contacts (${contacts.length})` },
                        { k: "visits", l: `Visites (${visits.length})` },
                        { k: "keywords", l: `Mots-clés (${keywords.length})` },
                    ].map((t) => (
                        <button key={t.k} onClick={() => setTab(t.k)}
                            data-testid={`tab-${t.k}`}
                            className={`px-4 py-2 text-xs uppercase tracking-wide font-semibold border-b-2 transition-colors ${
                                tab === t.k ? "border-[#E63946] text-[#E63946]" : "border-transparent text-zinc-500 hover:text-black"
                            }`}>{t.l}</button>
                    ))}
                </div>

                <input value={filter} onChange={(e) => setFilter(e.target.value)}
                    placeholder={tab === "keywords" ? "Filtrer par mot-clé, ville, page…" : tab === "contacts" ? "Filtrer par nom, ville, IP…" : "Filtrer par IP, ville, page, source…"}
                    className="w-full mb-4 border border-zinc-300 rounded px-4 py-2.5 text-sm focus:outline-none focus:border-black bg-white"
                    data-testid="admin-filter" />

                {tab !== "keywords" && (
                <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-zinc-200 rounded p-3" data-testid="admin-date-filter">
                    <label className="text-xs uppercase tracking-wide font-semibold text-zinc-600">📅 Jour :</label>
                    <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="border border-zinc-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-black"
                        data-testid="admin-date-input"
                    />
                    <button onClick={() => setDateFilter(todayYMD)}
                        className={`text-xs px-3 py-1.5 rounded uppercase tracking-wide font-semibold transition-colors ${dateFilter === todayYMD ? "bg-[#E63946] text-white" : "bg-zinc-100 hover:bg-zinc-200"}`}>
                        Aujourd'hui
                    </button>
                    <button onClick={() => setDateFilter(yesterdayYMD)}
                        className={`text-xs px-3 py-1.5 rounded uppercase tracking-wide font-semibold transition-colors ${dateFilter === yesterdayYMD ? "bg-[#E63946] text-white" : "bg-zinc-100 hover:bg-zinc-200"}`}>
                        Hier
                    </button>
                    <button onClick={() => setDateFilter("")}
                        className={`text-xs px-3 py-1.5 rounded uppercase tracking-wide font-semibold transition-colors ${dateFilter === "" ? "bg-[#0A0A0A] text-white" : "bg-zinc-100 hover:bg-zinc-200"}`}>
                        Tout afficher
                    </button>
                    {dateFilter && (
                        <span className="ml-auto text-xs text-zinc-600">
                            <strong>{finalRows.length}</strong> résultat{finalRows.length > 1 ? "s" : ""} pour le {new Date(dateFilter).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        </span>
                    )}
                </div>
                )}

                {loading && <div className="text-sm text-zinc-500">Chargement…</div>}

                {!loading && finalRows.length === 0 && (
                    <div className="bg-white border border-zinc-200 rounded p-8 text-center text-zinc-500 text-sm">
                        Aucun élément.
                    </div>
                )}

                {tab === "contacts" && (
                    <div className="space-y-3">
                        {finalRows.map((it) => (
                            <div key={it.id} className="bg-white border border-zinc-200 rounded-lg p-4 sm:p-5">
                                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                    <div>
                                        <div className="font-display font-bold text-base">{it.name}</div>
                                        <div className="text-xs text-zinc-500">{fmt(it.created_at)}</div>
                                    </div>
                                    <div className="text-right">
                                        <a href={`tel:${it.phone}`} className="block text-sm font-semibold text-[#E63946] hover:underline">{it.phone}</a>
                                        <a href={`mailto:${it.email}`} className="block text-xs text-zinc-600 hover:underline">{it.email}</a>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                                    <div className="bg-zinc-50 p-2.5 rounded"><div className="uppercase tracking-wide text-zinc-500 font-semibold mb-1">Ville déclarée</div><div className="font-semibold">{it.city || "—"}</div></div>
                                    <div className="bg-zinc-50 p-2.5 rounded"><div className="uppercase tracking-wide text-zinc-500 font-semibold mb-1">Service</div><div className="font-semibold">{it.service || "—"}</div></div>
                                    <div className="bg-zinc-50 p-2.5 rounded"><div className="uppercase tracking-wide text-zinc-500 font-semibold mb-1">IP</div><div className="font-mono font-semibold">{it.ip || "—"}</div></div>
                                    <div className="bg-zinc-50 p-2.5 rounded">
                                        <div className="uppercase tracking-wide text-zinc-500 font-semibold mb-1">Ville IP</div>
                                        <div className="font-semibold">{it.ip_city ? `${it.ip_city}${it.ip_region ? ", " + it.ip_region : ""}` : "—"}{it.ip_country ? <span className="text-zinc-500"> · {it.ip_country}</span> : null}</div>
                                        {it.ip_isp && <div className="text-zinc-500 mt-0.5">{it.ip_isp}</div>}
                                    </div>
                                </div>
                                {it.message && <div className="mt-3 text-sm bg-zinc-50 p-3 rounded whitespace-pre-wrap">{it.message}</div>}
                                <div className="mt-3 flex justify-end">
                                    <button
                                        onClick={() => deleteItem(it)}
                                        className="text-[10px] uppercase tracking-wide font-semibold text-zinc-400 hover:text-[#E63946] transition-colors"
                                        title="Supprimer ce contact"
                                        data-testid={`delete-contact-${it.id}`}
                                    >
                                        ✕ Supprimer
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "visits" && (
                    <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-zinc-50 border-b border-zinc-200">
                                <tr className="text-left">
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Date</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">IP</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Ville · Pays</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">📍 Position Google</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">FAI</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Page</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Source</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Mot-clé recherché</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {finalRows.map((v) => {
                                    const adsFlag = v.gclid || v.utm_source === "google" || v.utm_medium === "cpc";
                                    return (
                                        <tr key={v.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                                            <td className="px-3 py-2.5 whitespace-nowrap text-zinc-600">{fmt(v.created_at)}</td>
                                            <td className="px-3 py-2.5 font-mono">
                                                {v.ip || "—"}
                                                {(() => {
                                                    const useAds = quickView === "ads";
                                                    const count = useAds ? ipAdsCounts[v.ip] : ipCounts[v.ip];
                                                    if (!v.ip || !count || count <= 1) return null;
                                                    return (
                                                        <span
                                                            className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                                useAds ? "bg-[#E63946] text-white" : "bg-[#0A0A0A] text-white"
                                                            }`}
                                                            title={useAds
                                                                ? `Cette IP a cliqué ${count} fois sur une pub`
                                                                : `Cette IP a visité le site ${count} fois`}
                                                            data-testid={`ip-count-${v.ip}`}
                                                        >
                                                            × {count}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                {v.ip_city || "—"}
                                                {v.ip_country ? <span className="text-zinc-500"> · {v.ip_country}</span> : ""}
                                                {isMobileIsp(v.ip_isp) && (
                                                    <span
                                                        className="ml-1.5 inline-block bg-orange-100 text-orange-900 border border-orange-300 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide"
                                                        title="Connexion 4G/5G — la ville indiquée est administrative (souvent Paris/Marseille/Lyon) et ne reflète pas la position réelle du visiteur"
                                                        data-testid={`mobile-badge-${v.id}`}
                                                    >
                                                        📱 Mobile
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                {renderPositionCell(v)}
                                                {isMobileIsp(v.ip_isp) && (v.gps_lat == null || v.gps_lon == null) && (
                                                    <div className="text-[9px] text-orange-700 mt-0.5 italic" title="L'IP mobile pointe vers l'adresse administrative de l'opérateur, pas vers la position réelle.">
                                                        ⚠ approximative (4G/5G)
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 text-zinc-600">{v.ip_isp || "—"}</td>
                                            <td className="px-3 py-2.5 font-mono text-zinc-700">{v.path}</td>
                                            <td className="px-3 py-2.5">
                                                {renderSourceCell(v, adsFlag)}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                {renderKeywordCell(v, adsFlag)}
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                <button
                                                    onClick={() => deleteItem(v)}
                                                    className="text-[10px] uppercase tracking-wide font-semibold text-zinc-400 hover:text-[#E63946] transition-colors"
                                                    title="Supprimer cette visite"
                                                    data-testid={`delete-visit-${v.id}`}
                                                >
                                                    ✕ Suppr.
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {tab === "keywords" && finalRows.length > 0 && (
                    <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="keywords-table">
                        <div className="px-4 py-3 bg-[#FFF5F6] border-b border-zinc-200 text-xs text-zinc-600">
                            <strong className="text-[#E63946]">ℹ Mots-clés Google réels par ville.</strong> Les mots-clés proviennent de vos campagnes Google Ads (paramètre <code className="font-mono bg-white px-1 rounded">utm_term</code> / mot-clé transmis). Google ne transmet pas les mots-clés des résultats naturels (organiques) — seuls les clics publicitaires sont détaillés ici.
                        </div>
                        <table className="w-full text-xs">
                            <thead className="bg-zinc-50 border-b border-zinc-200">
                                <tr className="text-left">
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Mot-clé recherché</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Ville du visiteur</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600 text-right">Visites</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600 text-right">Dont Ads</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Pages visitées</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wide text-zinc-600">Dernière visite</th>
                                </tr>
                            </thead>
                            <tbody>
                                {finalRows.map((k) => (
                                    <tr key={`${k.keyword}::${k.city}`} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`keyword-row-${k.keyword.replace(/\s+/g, "-")}`}>
                                        <td className="px-3 py-2.5">
                                            <span className="inline-block bg-yellow-100 text-yellow-900 px-2 py-0.5 rounded text-xs font-semibold">{k.keyword}</span>
                                        </td>
                                        <td className="px-3 py-2.5 font-semibold">{k.city}</td>
                                        <td className="px-3 py-2.5 text-right font-display font-bold">{k.count}</td>
                                        <td className="px-3 py-2.5 text-right">
                                            {k.ads_count > 0
                                                ? <span className="inline-block bg-[#E63946] text-white px-2 py-0.5 rounded text-[10px] font-semibold">{k.ads_count}</span>
                                                : <span className="text-zinc-400">0</span>}
                                        </td>
                                        <td className="px-3 py-2.5 font-mono text-zinc-600">{(k.paths || []).join(", ")}</td>
                                        <td className="px-3 py-2.5 whitespace-nowrap text-zinc-600">{fmt(k.last_seen)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Admin;
